import { CommonModule } from '@angular/common';
import { ChangeDetectorRef, Component, DestroyRef, ElementRef, HostListener, ViewChild, inject, OnInit } from '@angular/core';
import { FormsModule, ReactiveFormsModule, UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { finalize, firstValueFrom } from 'rxjs';

import { ApiMethod, AuthEndPoints } from '../../../helper/apiconstant';
import { DEFAULT_DASHBOARD_USER } from '../../dashboard/dashboard.data';
import { ClickOutsideDirective } from '../../../helper/directives/click-outside.directive';
import { AppPreferencesService } from '../../../services/app-preferences';
import { Httpservice } from '../../../services/httpservice';
import { SocketService, SocketServerEvent } from '../../../services/socket.service';
import { ServerTimeService } from '../../../services/server-time';
import { ReportShellComponent } from '../../structure/report-shell/report-shell';
import { getTodayDateValue, resolvePeriodForBlockLabel } from './peak-report.data';
import {
  applyPreviousValueMode,
  buildReportBlockGroups,
  buildEditDataFormValue,
  clearTruthyFormControls,
  downloadReportBlob,
  extractReportBsCalendar,
  extractReportDateConversion,
  formatReportNumber,
  buildGroupedReportHierarchyNodes,
  buildLinkedReportHierarchyNodes,
  flattenReportHierarchyNodes,
  getReportCellText,
  getNullableNumberValue,
  getReportDateLabel,
  getReportHierarchyNodeOrderKey,
  getReportRowBlockExtremeValue,
  getDefaultReportDate,
  normalizeReportShowValues,
  ReportDataParsingBase,
  resetReportEditForm,
  extractReportSummary,
  extractReportBlockDefinitions,
  isMissingReportHierarchyLabel,
  normalizeReportHierarchyLabel,
  normalizeOptionalReportHierarchyLabel,
  isFuelReportHierarchyLabel,
  resolveReportBlockPeriod,
  extractReportRows,
  initializeReportPage,
  bindReportFilterListeners,
  listenToReportPreferenceChanges,
  captureReportViewState,
  restoreReportViewState,
  isCurrentBsReportDateSelected,
  isCurrentAdReportDateSelected,
  handleReportEditDataValueInput,
  shouldCloseReportDatePicker,
  openReportDatePicker,
  loadReportBsCalendar,
  handleReportDateSelection,
  buildReportExportFileName,
  getReportSelectedDateFormValue,
  updateReportCellValue,
  submitReportEdit,
  patchReportPreviousValue,
  cloneReportRowWithBlocks,
  aggregateReportRows,
  listenToReportSocketEvents,
  hasTextValue,
  stripTrailingCapacityLabel,
  isLogsheetDataDetailCell,
  shouldFetchPreviousValue,
  toggleReportHierarchyRow,
  sortReportGeneratorNodesByInstalledCapacity,
} from '../shared/report-ui.helpers';
import {
  PeakReportBlockCell,
  PeakReportBlockDefinition,
  PeakReportBsCalendarCell,
  PeakReportBsCalendarResponse,
  PeakReportPreviousBlockValueResponse,
  PeakReportReportResponse,
  PeakReportRow,
  PeakReportSummary,
} from './peak-report.interfaces';

interface PeakReportHierarchyNode {
  row: PeakReportRow;
  children: PeakReportHierarchyNode[];
}

interface PeakReportBlockGroup {
  period: PeakReportBlockDefinition['period'];
  label: string;
  span: number;
}

type PeakReportViewTab = 'generation' | 'summary';
type PeakReportDisplayMode = 'generation' | 'spill' | 'water-level' | 'interruption' | 'summary';

const SUMMARY_ACCORDION_ORDER = [
  'sector:nea',
  'row:power-total-thermal',
  'sector:nea-subsidiary',
  'sector:ipp',
  'row:power-total-import',
  'row:power-total-export',
  'row:power-net-import',
  'row:power-system-load-actual',
  'row:power-interruption-tripping',
  'row:power-system-load-expected',
  'row:power-national-load-actual',
  'row:power-line-flows',
  'row:power-frequency-voltage-profile',
  'row:power-frequency-voltage-substation',
  'row:power-kabeli-corridor',
  'row:power-kathmandu-load',
  'row:power-spill',
  'row:power-water-level',
] as const;
const SUMMARY_ACCORDION_ORDER_MAP = new Map<string, number>(
  SUMMARY_ACCORDION_ORDER.map((key, index) => [key, index]),
);

@Component({
  selector: 'app-peak-report-page',
  standalone: true,
  imports: [CommonModule, FormsModule, ReactiveFormsModule, ReportShellComponent, ClickOutsideDirective],
  templateUrl: './peak-report.html',
  styleUrl: './peak-report.scss',
})
export class PeakReportPage extends ReportDataParsingBase implements OnInit {
  protected readonly user = DEFAULT_DASHBOARD_USER;
  protected readonly filtersForm: UntypedFormGroup;

  protected isLoading = false;
  protected isExporting = false;
  protected isSavingCell = false;
  protected selectedViewTab: PeakReportViewTab = 'summary';
  protected detailMode: PeakReportDisplayMode = 'summary';

  protected blockColumns: PeakReportBlockDefinition[] = [];
  protected blockGroups: PeakReportBlockGroup[] = [];
  protected rows: PeakReportRow[] = [];
  protected summary: PeakReportSummary = {
    scheduled_mwh: 0,
    actual_mwh: 0,
    deviation_mwh: 0,
    row_count: 0,
  };

  protected bsCalendarOpen = false;
  protected bsCalendarLoading = false;
  protected bsCalendarMonth: PeakReportBsCalendarResponse | null = null;
  protected readonly bsCalendarWeekdays = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

  protected editDataModalOpen = false;
  protected remarksForm: UntypedFormGroup;
  protected socketId: string | null = null;

  @ViewChild('tableWrap') private readonly tableWrap?: ElementRef<HTMLDivElement>;
  @ViewChild('hiddenDateInput') private readonly hiddenDateInput?: ElementRef<HTMLInputElement>;
  @ViewChild('bsDatePickerShell') private readonly bsDatePickerShell?: ElementRef<HTMLDivElement>;

  private readonly destroyRef = inject(DestroyRef);
  private readonly cdr = inject(ChangeDetectorRef);
  private hierarchyRoots: PeakReportHierarchyNode[] = [];
  private reportRowsCache: PeakReportRow[] = [];
  private expandedRowKeys = new Set<string>();
  private pendingViewStateRestore: {
    expandedRowKeys: string[];
    scrollTop: number;
    scrollLeft: number;
  } | null = null;
  private pendingRemarksContext: {
    station_id: number;
    block_no: number;
    block_value: number | null;
    remarks: string | null;
  } | null = null;

  constructor(
    private readonly fb: UntypedFormBuilder,
    private readonly httpSvc: Httpservice,
    private readonly preferencesSvc: AppPreferencesService,
    private readonly serverTimeSvc: ServerTimeService,
    private readonly socketSvc: SocketService,
  ) {
    super();
    this.filtersForm = this.fb.group({
      date: [getDefaultReportDate(getTodayDateValue()), Validators.required],
      show_peak: ['Overall Peak'],
      substation_id: [null],
      show_values: [null],
    });
    this.remarksForm = this.fb.group({
      data_value: [''],
      copy_previous_block_value: [false],
      carry_over_previous_day_values: [false],
      remarks: [''],
    });
  }

  async ngOnInit(): Promise<void> {
    await initializeReportPage({
      applyTemporaryDefaultDate: () => this.applyTemporaryReportDate(this.filtersForm, getTodayDateValue()),
      bindFilterListeners: () => this.bindFilterListeners(),
      listenToSocketEvents: () => this.listenToSocketEvents(),
      listenToPreferenceChanges: () => this.listenToPreferenceChanges(),
      handleDateSelectionChange: () => this.handleDateSelectionChange(),
    });
  }


  protected clearFilters(): void {
    this.filtersForm.patchValue(
      {
        date: getDefaultReportDate(getTodayDateValue()),
        show_peak: 'Overall Peak',
        substation_id: null,
        show_values: null,
      },
      { emitEvent: false },
    );
    this.bsCalendarOpen = false;
    void this.handleDateSelectionChange();
  }

  protected handleExportToExcel(): void {
    if (this.isExporting) {
      return;
    }

    const payload = this.buildReportPayload();
    this.isExporting = true;

    this.httpSvc
      .downloadfile(AuthEndPoints.EXPORT_PEAK_REPORT, ApiMethod.POST, payload)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => {
          this.isExporting = false;
          this.cdr.detectChanges();
        }),
      )
      .subscribe({
        next: (blob) => downloadReportBlob(blob, buildReportExportFileName(this.getSelectedDateValue())),
      });
  }

  protected trackByBlock(_: number, item: PeakReportBlockDefinition): number {
    return item.block_no;
  }

  protected trackByBlockGroup(_: number, item: PeakReportBlockGroup): string {
    return `${item.period}-${item.span}`;
  }

  protected trackByRow(_: number, item: PeakReportRow): string {
    return item.row_key ?? `${item.row_kind ?? 'data'}-${item.station_id}`;
  }

  protected getSelectedShowValuesLabel(): string {
    const value = this.filtersForm.get('show_values')?.value;
    const label = typeof value === 'string' && value ? value : 'Select Value';
    return label;
  }

  protected getFilterDateLabel(): string {
    return getReportDateLabel(this.selectedDateDisplayLabel, this.selectedDateRequestValue, this.selectedDateAdValue);
  }

  protected getHeaderSummary(): string {
    const count = this.summary.row_count;
    const blockCount = this.blockColumns.length;
    return `Showing ${count} generator${count === 1 ? '' : 's'} • ${blockCount} peak block${blockCount === 1 ? '' : 's'}`;
  }

  protected isPeakBlock(cell: PeakReportBlockCell): boolean {
    return cell.is_peak === true;
  }

  protected get isBsDateMode(): boolean {
    return this.preferencesSvc.showBsDate;
  }

  protected setViewTab(viewTab: PeakReportViewTab): void {
    const shouldResetAccordionState = this.selectedViewTab !== viewTab;
    this.selectedViewTab = viewTab;
    this.pendingViewStateRestore = null;
    this.detailMode = viewTab === 'summary' ? 'summary' : 'generation';
    if (!shouldResetAccordionState) {
      return;
    }

    this.loadReport(this.rows.length > 0);
  }

  protected handleDetailModeChange(mode: PeakReportDisplayMode): void {
    if (this.detailMode === mode) {
      return;
    }

    this.detailMode = mode;
    if (mode === 'summary') {
      this.setViewTab('summary');
      return;
    }

    if (this.selectedViewTab === 'summary') {
      this.setViewTab('generation');
    }

    this.loadReport(this.rows.length > 0);
  }

  protected isDetailModeActive(mode: PeakReportDisplayMode): boolean {
    return this.detailMode === mode;
  }

  protected getCellText(cell: PeakReportBlockCell): string {
    return getReportCellText(cell);
  }

  protected isEditedCell(cell: PeakReportBlockCell): boolean {
    return isLogsheetDataDetailCell(cell);
  }

  protected getTotalCellText(value: number): string {
    return this.formatNumber(value);
  }

  protected getRowMaxText(row: PeakReportRow): string {
    return this.formatNumber(this.getRowBlockMaxValue(row));
  }

  protected getRowMinText(row: PeakReportRow): string {
    return this.formatNumber(this.getRowBlockMinValue(row));
  }

  protected getInstalledCapacityText(row: PeakReportRow): string {
    return this.formatNumber(row.installed_capacity ?? 0);
  }

  protected isCellInteractive(row: PeakReportRow, cell: PeakReportBlockCell): boolean {
    return false;
  }

  protected isSectorTotalRow(row: PeakReportRow): boolean {
    return this.isHierarchyTotalRow(row);
  }

  protected isHierarchyTotalRow(row: PeakReportRow): boolean {
    if (row.has_children) {
      return true;
    }

    return !this.isDetailRowKind(row.row_kind);
  }

  protected hasHierarchyChildren(row: PeakReportRow): boolean {
    if (!row.row_key || !row.has_children) {
      return false;
    }

    return true;
  }

  protected isHierarchyExpanded(row: PeakReportRow): boolean {
    if (!row.row_key) {
      return false;
    }

    return this.expandedRowKeys.has(row.row_key);
  }

  protected toggleHierarchyRow(row: PeakReportRow): void {
    if (!toggleReportHierarchyRow(row, this.expandedRowKeys)) {
      return;
    }

    this.rows = this.buildRowsFromCurrentState();
  }

  protected beginCellEdit(row: PeakReportRow, cell: PeakReportBlockCell): void {
    if (!this.isCellInteractive(row, cell)) {
      return;
    }

    this.openEditDataModal(row, cell);
  }

  protected closeEditDataModal(): void {
    this.editDataModalOpen = false;
    this.pendingRemarksContext = null;
    resetReportEditForm(this.remarksForm);
  }

  protected saveEditData(): void {
    if (!this.pendingRemarksContext) {
      this.closeEditDataModal();
      return;
    }

    const context = { ...this.pendingRemarksContext };
    const formValue = this.remarksForm.getRawValue();
    const remarks = typeof formValue.remarks === 'string' ? formValue.remarks.trim() : '';
    const dataValue = this.normalizeNullableNumber(formValue.data_value);
    const payload = {
      data_date: this.getSelectedDateValue(),
      generator_id: context.station_id,
      block_no: context.block_no,
      block_value: dataValue,
      remarks: remarks || null,
      detail_mode: this.detailMode,
    };

    this.updateLocalCellValue(context.station_id, context.block_no, dataValue, remarks || null);
    submitReportEdit({
      request$: this.httpSvc.requestCall(AuthEndPoints.UPDATE_LOG_SHEET_CELL, ApiMethod.PATCH, payload, true),
      destroyRef: this.destroyRef,
      setSaving: (saving) => {
        this.isSavingCell = saving;
      },
      detectChanges: () => this.cdr.detectChanges(),
      onSuccess: () => {
        this.closeEditDataModal();
        this.loadReport(true);
      },
      onError: () => this.loadReport(true),
    });
  }

  protected onEditDataValueInput(event: Event): void {
    handleReportEditDataValueInput(event, (controlNames) => {
      clearTruthyFormControls(this.remarksForm, controlNames);
    });
  }

  protected onPreviousValueModeChange(mode: 'copy' | 'carry'): void {
    applyPreviousValueMode(this.remarksForm, mode);
  }

  protected arePreviousValueControlsDisabled(): boolean {
    const dataValue = this.remarksForm.get('data_value')?.value;
    return hasTextValue(dataValue);
  }

  protected onCopyPreviousBlockValueChange(): void {
    const context = this.pendingRemarksContext;
    if (!shouldFetchPreviousValue(this.remarksForm, 'copy_previous_block_value', 'carry_over_previous_day_values', Boolean(context)) || !context) {
      return;
    }

    this.fetchPreviousBlockValue(context.station_id, context.block_no);
  }

  protected onCarryOverPreviousDayValuesChange(): void {
    const context = this.pendingRemarksContext;
    if (!shouldFetchPreviousValue(this.remarksForm, 'carry_over_previous_day_values', 'copy_previous_block_value', Boolean(context)) || !context) {
      return;
    }

    this.fetchPreviousDayBlockValue(context.station_id, context.block_no);
  }

  protected formatNumber(value: number | null): string {
    return formatReportNumber(value);
  }

  protected getEmptyColspan(): number {
    return this.blockColumns.length + 1;
  }

  @HostListener('document:mousedown', ['$event'])
  protected handleDocumentMouseDown(event: MouseEvent): void {
    if (shouldCloseReportDatePicker(this.bsCalendarOpen, event, this.bsDatePickerShell?.nativeElement ?? null)) {
      this.closeBsDatePicker();
    }
  }

  private collapseAllAccordions(): void {
    this.expandedRowKeys.clear();
    this.rows = this.buildRowsFromCurrentState();
  }

  private bindFilterListeners(): void {
    bindReportFilterListeners(
      this.filtersForm,
      this.destroyRef,
      ['substation_id', 'show_values', 'show_peak'],
      () => this.handleDateSelectionChange(this.rows.length > 0),
      () => this.loadReport(this.rows.length > 0),
      () => this.onCopyPreviousBlockValueChange(),
      () => this.onCarryOverPreviousDayValuesChange(),
    );
  }

  private listenToSocketEvents(): void {
    listenToReportSocketEvents(
      this.socketSvc.events$,
      this.socketSvc.socketId$,
      this.destroyRef,
      (event) => this.handleSocketEvent(event),
      (socketId) => {
        this.socketId = socketId;
        this.cdr.detectChanges();
      },
    );
  }

  private listenToPreferenceChanges(): void {
    listenToReportPreferenceChanges(this.destroyRef, () => {
      this.closeBsDatePicker();
      this.handleDateSelectionChange(this.rows.length > 0);
    });
  }

  private handleSocketEvent(event: SocketServerEvent): void {
    if (event.event === 'scada_sync_completed') {
      this.loadReport(true);
    }
  }

  private loadReport(preserveViewState = false): void {
    if (preserveViewState) {
      this.captureViewStateForRestore();
    } else {
      this.pendingViewStateRestore = null;
    }

    const payload = this.buildReportPayload();
    this.isLoading = true;

    this.httpSvc
      .requestCall(AuthEndPoints.GET_PEAK_REPORT, ApiMethod.POST, payload, false, { suppressToast: true })
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => {
          this.isLoading = false;
          this.cdr.detectChanges();
        }),
      )
      .subscribe({
        next: (response) => {
          const report = this.extractReport(response);
          this.applyReport(report);
        },
        error: () => {
          if (preserveViewState) {
            this.restoreViewStateAfterRefresh();
            return;
          }

          this.applyReport(null);
        },
      });
  }

  private isDetailRowKind(rowKind: PeakReportRow['row_kind']): boolean {
    return rowKind === 'data' || rowKind === 'interruption-line' || rowKind === 'interruption-substation' ||
      rowKind === 'power-thermal-generator' || rowKind === 'power-water-level-generator' ||
      rowKind === 'power-kabeli-corridor-generator' || rowKind === 'frequency-voltage-substation';
  }

  private applyReport(report: PeakReportReportResponse | null): void {
    if (!report) {
      this.blockColumns = [];
      this.blockGroups = [];
      this.rows = [];
      this.hierarchyRoots = [];
      this.reportRowsCache = [];
      this.expandedRowKeys.clear();
      this.summary = {
        scheduled_mwh: 0,
        actual_mwh: 0,
        deviation_mwh: 0,
        row_count: 0,
      };
      return;
    }

    this.blockColumns = report.blocks;
    this.blockGroups = this.buildBlockGroups(report.blocks);
    this.reportRowsCache = report.rows.map((row) => this.cloneReportRow(row));
    this.hierarchyRoots = this.buildHierarchyNodes(this.reportRowsCache.filter((row) => !row.force_flat || this.isPowerNestedRow(row)));
    const shouldRestoreViewState = Boolean(this.pendingViewStateRestore);
    this.expandedRowKeys = shouldRestoreViewState && this.pendingViewStateRestore
      ? new Set(this.pendingViewStateRestore.expandedRowKeys)
      : this.getDefaultExpandedRowKeys(this.hierarchyRoots);
    if (this.selectedViewTab === 'summary') {
      this.expandedRowKeys = this.getCollapsedSummaryRowKeys();
    }
    this.rows = this.buildRowsFromCurrentState();
    this.summary = report.summary;
    if (!this.preferencesSvc.showBsDate && report.date_label) {
      this.selectedDateDisplayLabel = report.date_label;
    }

    if (shouldRestoreViewState) {
      this.restoreViewStateAfterRefresh();
    } else {
      this.pendingViewStateRestore = null;
    }
  }

  private openEditDataModal(row: PeakReportRow, cell: PeakReportBlockCell): void {
    this.pendingRemarksContext = {
      station_id: row.station_id,
      block_no: cell.block_no,
      block_value: getNullableNumberValue(cell.value),
      remarks: cell.remarks ?? null,
    };
    this.remarksForm.reset(buildEditDataFormValue(cell.value, cell.remarks), { emitEvent: false });
    this.editDataModalOpen = true;
  }

  private fetchPreviousBlockValue(stationId: number, blockNo: number): void {
    this.fetchPreviousValue(AuthEndPoints.GET_LOG_SHEET_PREVIOUS_BLOCK_VALUE, stationId, blockNo, 'copy_previous_block_value');
  }

  private fetchPreviousDayBlockValue(stationId: number, blockNo: number): void {
    this.fetchPreviousValue(AuthEndPoints.GET_LOG_SHEET_PREVIOUS_DAY_BLOCK_VALUE, stationId, blockNo, 'carry_over_previous_day_values');
  }

  private fetchPreviousValue(endpoint: string, stationId: number, blockNo: number, resetControlName: string): void {
    const payload = {
      data_date: this.getSelectedDateValue(),
      generator_id: stationId,
      block_no: blockNo,
      bs_ad_key: 'AD',
    };

    this.httpSvc
      .requestCall(endpoint, ApiMethod.POST, payload, false, { suppressToast: true })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response) => {
          this.applyPreviousBlockValue(response);
        },
        error: () => {
          this.remarksForm.patchValue({ [resetControlName]: false }, { emitEvent: false });
        },
      });
  }

  private applyPreviousBlockValue(response: unknown): void {
    const previous = this.extractPreviousBlockValue(response);
    patchReportPreviousValue(this.remarksForm, previous);
  }

  private updateLocalCellValue(stationId: number, blockNo: number, blockValue: number | null, remarks: string | null): void {
    this.reportRowsCache = updateReportCellValue({
      hierarchyRoots: this.hierarchyRoots,
      cachedRows: this.reportRowsCache,
      stationId,
      blockNo,
      blockValue,
      remarks,
    });
    this.rows = this.buildRowsFromCurrentState();
  }

  private cloneReportRow(row: PeakReportRow): PeakReportRow {
    return cloneReportRowWithBlocks(row);
  }

  private extractPreviousBlockValue(response: unknown): PeakReportPreviousBlockValueResponse | null {
    const value = this.extractResponseValue(response);
    if (!value) {
      return null;
    }

    return {
      data_value: this.normalizeNullableNumber(value['data_value']),
      remarks: this.extractStringValue(value, 'remarks'),
      previous_block_no: this.normalizeNullableNumber(value['previous_block_no']),
      source: this.extractStringValue(value, 'source'),
    };
  }

  private buildReportPayload(): Record<string, unknown> {
    return {
      data_date: this.getSelectedDateValue(),
      entity_id: null,
      generation_id: this.normalizeNumber(this.filtersForm.get('substation_id')?.value),
      show_values: this.normalizeSelectedShowValues(),
      show_peak: this.filtersForm.get('show_peak')?.value || 'Overall Peak',
      detail_mode: this.detailMode,
      bs_ad_key: 'AD',
      selected_view_tab: this.selectedViewTab,
    };
  }

  private normalizeSelectedShowValues(): string | null {
    return normalizeReportShowValues(this.filtersForm.get('show_values')?.value);
  }

  private captureViewStateForRestore(): void {
    this.pendingViewStateRestore = captureReportViewState(this.tableWrap?.nativeElement ?? null, this.expandedRowKeys);
  }

  private restoreViewStateAfterRefresh(): void {
    const snapshot = this.pendingViewStateRestore;
    if (!snapshot) {
      return;
    }
    restoreReportViewState(snapshot, (expandedRowKeys, viewState) => {
      this.expandedRowKeys = expandedRowKeys;
      this.rows = this.buildRowsFromCurrentState();

      const tableWrap = this.tableWrap?.nativeElement ?? null;
      if (tableWrap) {
        tableWrap.scrollTop = viewState.scrollTop;
        tableWrap.scrollLeft = viewState.scrollLeft;
      }

    }, () => {
      this.pendingViewStateRestore = null;
    }, () => this.cdr.detectChanges());
  }

  private isCurrentDateSelected(): boolean {
    if (this.preferencesSvc.showBsDate) {
      return isCurrentBsReportDateSelected(this.getSelectedDateValue(), this.serverTimeSvc.getCurrentBsDateValue());
    }
    return isCurrentAdReportDateSelected(this.getSelectedDateValue(), getTodayDateValue());
  }

  private buildHierarchyNodes(rows: PeakReportRow[]): PeakReportHierarchyNode[] {
    const normalizedRows: PeakReportRow[] = rows.map((row) => ({
      ...row,
      row_kind: this.isPowerNestedRow(row) || row.parent_row_key ? row.row_kind : 'data',
      row_level: this.isPowerNestedRow(row) || row.parent_row_key ? row.row_level : 3,
      row_key: this.isPowerNestedRow(row) || row.parent_row_key ? row.row_key : `data-${row.station_id}`,
      has_children: false,
    }));

    const specialRows = normalizedRows.filter((row) => this.isPowerNestedRow(row));
    const hierarchyNodes = buildGroupedReportHierarchyNodes({
      rows: normalizedRows.filter((row) => !this.isPowerNestedRow(row)),
      isSummaryView: this.selectedViewTab === 'summary',
      normalizeHierarchyLabel: (value) => this.normalizeHierarchyLabel(value),
      normalizeOptionalHierarchyLabel: (value) => this.normalizeOptionalHierarchyLabel(value),
      isFuelHierarchyLabel: (value) => this.isFuelHierarchyLabel(value),
      isMissingHierarchyLabel: (value) => this.isMissingHierarchyLabel(value),
      sortNodes: (nodes) => this.sortHierarchyNodesForCurrentMode(nodes),
      buildAggregateRow: (rowKey, label, aggregateRows, rowLevel, rowKind, hasChildren, forceExpandChildren) =>
        this.buildAggregateRow(rowKey, label, aggregateRows, rowLevel, rowKind, hasChildren, forceExpandChildren),
    });

    return this.attachSpecialPowerRows(hierarchyNodes, specialRows);
  }

  private attachSpecialPowerRows(nodes: PeakReportHierarchyNode[], specialRows: PeakReportRow[]): PeakReportHierarchyNode[] {
    const smallRows = specialRows.filter((row) => row.row_kind === 'power-small');
    const thermalRow = specialRows.find((row) => row.row_key === 'power-total-thermal');
    const thermalChildren = specialRows.filter((row) => row.row_kind === 'power-thermal-generator');

    for (const row of smallRows) {
      const ownerNode = this.findSectorNode(nodes, row.owner);
      const hydroNode = ownerNode?.children.find(
        (node) => node.row.row_kind === 'source-total' && node.row.station_name.toLowerCase().startsWith('total hydro'),
      );
      if (!hydroNode) {
        continue;
      }
      hydroNode.row.has_children = true;
      const lastFuelIndex = hydroNode.children.reduce(
        (lastIndex, child, index) => child.row.row_kind === 'subcat-total' ? index : lastIndex,
        -1,
      );
      hydroNode.children.splice(lastFuelIndex + 1, 0, { row: { ...row, row_level: 2 }, children: [] });
    }

    if (thermalRow) {
      const neaNode = this.findSectorNode(nodes, 'NEA');
      if (neaNode) {
        neaNode.row.installed_capacity = Number(((neaNode.row.installed_capacity ?? 0) + (thermalRow.installed_capacity ?? 0)).toFixed(5));
        const neaLabel = stripTrailingCapacityLabel(neaNode.row.station_name);
        neaNode.row.station_name = `${neaLabel} (${this.formatNumber(neaNode.row.installed_capacity)})`;
        const solarIndex = neaNode.children.findIndex(
          (node) => node.row.row_kind === 'source-total' && node.row.station_name.toLowerCase().startsWith('total solar'),
        );
        neaNode.children.splice(solarIndex < 0 ? neaNode.children.length : solarIndex + 1, 0, {
          row: { ...thermalRow, station_name: `${thermalRow.station_name} (${this.formatNumber(thermalRow.installed_capacity ?? 0)})`, row_level: 1, has_children: thermalChildren.length > 0 },
          children: thermalChildren.map((row) => ({ row: { ...row, row_level: 2 }, children: [] })),
        });
      }
    }

    return nodes;
  }

  private findSectorNode(nodes: PeakReportHierarchyNode[], owner: string | null | undefined): PeakReportHierarchyNode | null {
    const normalizedOwner = (owner ?? '').trim().toLowerCase();
    for (const node of nodes) {
      const label = stripTrailingCapacityLabel(node.row.station_name).trim().toLowerCase();
      if (node.row.row_kind === 'sector-total' && label === `total ${normalizedOwner}`) {
        return node;
      }
      const nestedNode = this.findSectorNode(node.children, owner);
      if (nestedNode) {
        return nestedNode;
      }
    }
    return null;
  }

  private isPowerNestedRow(row: PeakReportRow): boolean {
    return row.row_kind === 'power-small' || row.row_kind === 'power-thermal-generator' || row.row_key === 'power-total-thermal';
  }

  protected isHydroSmallPowerRow(row: PeakReportRow): boolean {
    return Boolean(row.is_small_power) && row.source_category?.trim().toLowerCase() === 'hydro';
  }

  protected isKathmanduLoadGenerator(row: PeakReportRow): boolean {
    return row.row_kind === 'data' && (row.other_parameter ?? '').toLowerCase().replace(/\s/g, '').includes('kathmanduload');
  }

  private buildRowsFromCurrentState(): PeakReportRow[] {
    const flatNodes = this.sortHierarchyNodesForCurrentMode(this.buildFlatSummaryNodes());
    const rootNodes = this.selectedViewTab === 'summary'
      ? this.sortHierarchyNodesForCurrentMode([...this.hierarchyRoots, ...flatNodes])
      : [...this.hierarchyRoots, ...flatNodes];

    return [
      ...this.flattenHierarchyNodes(rootNodes, this.expandedRowKeys),
    ];
  }

  private buildFlatSummaryNodes(): PeakReportHierarchyNode[] {
    return this.buildLinkedRowNodes(this.reportRowsCache.filter((row) => row.force_flat && !this.isPowerNestedRow(row)));
  }

  private buildLinkedRowNodes(rows: PeakReportRow[]): PeakReportHierarchyNode[] {
    return buildLinkedReportHierarchyNodes(rows, (nodes) => this.sortHierarchyNodesForCurrentMode(nodes));
  }

  private sortHierarchyNodesForCurrentMode(nodes: PeakReportHierarchyNode[]): PeakReportHierarchyNode[] {
    const orderedNodes = this.selectedViewTab === 'summary'
      ? [...nodes].sort((left, right) => this.getHierarchyNodeOrder(left) - this.getHierarchyNodeOrder(right))
      : nodes;
    return sortReportGeneratorNodesByInstalledCapacity(orderedNodes);
  }

  private getHierarchyNodeOrder(node: PeakReportHierarchyNode): number {
    const key = this.getHierarchyNodeOrderKey(node.row);
    return SUMMARY_ACCORDION_ORDER_MAP.get(key) ?? SUMMARY_ACCORDION_ORDER_MAP.size + 100;
  }

  private getHierarchyNodeOrderKey(row: PeakReportRow): string {
    return getReportHierarchyNodeOrderKey(row);
  }

  private buildAggregateRow(
    rowKey: string,
    label: string,
    rows: PeakReportRow[],
    rowLevel: number,
    rowKind: 'sector-total' | 'ipp-total' | 'source-total' | 'subcat-total',
    hasChildren: boolean,
    forceExpandChildren = false,
  ): PeakReportRow {
    const aggregate = aggregateReportRows(rows, this.blockColumns);
    const installedCapacityLabel = this.formatNumber(aggregate.installed_capacity);
    const blocks = aggregate.blocks.map((block) => ({
      ...block,
      remarks: null,
      source: null,
      editable_id: null,
      is_peak: this.blockColumns.find((item) => item.block_no === block.block_no)?.is_peak === true,
    }));

    return {
      station_id: 0,
      station_name: `${label} (${installedCapacityLabel})`,
      station_code: null,
      owner: null,
      source_category: null,
      sub_category: null,
      blocks,
      scheduled_mwh: aggregate.scheduled_mwh,
      actual_mwh: aggregate.actual_mwh,
      deviation_mwh: aggregate.deviation_mwh,
      installed_capacity: aggregate.installed_capacity,
      row_key: rowKey,
      row_kind: rowKind,
      row_level: rowLevel,
      has_children: hasChildren,
      force_expand_children: forceExpandChildren,
    };
  }

  private getRowBlockMaxValue(row: PeakReportRow): number | null {
    return getReportRowBlockExtremeValue(row, 'max');
  }

  private getRowBlockMinValue(row: PeakReportRow): number | null {
    return getReportRowBlockExtremeValue(row, 'min');
  }

  private flattenHierarchyNodes(nodes: PeakReportHierarchyNode[], expandedKeys: Set<string>): PeakReportRow[] {
    return flattenReportHierarchyNodes(nodes, expandedKeys);
  }

  private getDefaultExpandedRowKeys(nodes: PeakReportHierarchyNode[]): Set<string> {
    const expandedKeys = new Set<string>();

    if (this.selectedViewTab === 'summary') {
      return expandedKeys;
    }

    for (const node of nodes) {
      if (!node.row.has_children || !node.row.row_key) {
        continue;
      }

      if ((node.row.row_level ?? 0) <= 1) {
        expandedKeys.add(node.row.row_key);
      }

      for (const child of node.children) {
        if (child.row.row_key && this.shouldExpandPowerModeChild(child)) {
          expandedKeys.add(child.row.row_key);
        }
      }
    }

    return expandedKeys;
  }

  private getCollapsedSummaryRowKeys(): Set<string> {
    return new Set<string>();
  }

  private shouldExpandPowerModeChild(child: PeakReportHierarchyNode): boolean {
    return Boolean(
      child.row.has_children &&
      child.row.row_key &&
      (child.row.row_level ?? 0) <= 1 &&
      this.hasSubcategoryChildren(child),
    );
  }

  private hasSubcategoryChildren(node: PeakReportHierarchyNode): boolean {
    return node.children.some((grandchild) => grandchild.row.row_kind === 'subcat-total');
  }

  private normalizeHierarchyLabel(value: string | null | undefined): string {
    const candidate = normalizeReportHierarchyLabel(value);
    if (candidate.toUpperCase() === 'NEA SUBSIDIARY') {
      return 'NEA Subsidiary';
    }
    return candidate;
  }

  private normalizeOptionalHierarchyLabel(value: string | null | undefined): string | null {
    return normalizeOptionalReportHierarchyLabel(value);
  }

  private isFuelHierarchyLabel(value: string | null | undefined): boolean {
    return isFuelReportHierarchyLabel(value);
  }

  private isMissingHierarchyLabel(value: string | null | undefined): boolean {
    return isMissingReportHierarchyLabel(value);
  }

  private buildBlockGroups(blocks: PeakReportBlockDefinition[]): PeakReportBlockGroup[] {
    return buildReportBlockGroups(blocks, (period) => this.getPeriodTitle(period));
  }

  private getPeriodTitle(period: PeakReportBlockDefinition['period']): string {
    switch (period) {
      case 'morning':
        return 'Morning Peak';
      case 'day':
        return 'Source Peak';
      case 'night':
        return 'Night Peak';
      default:
        return 'Blocks';
    }
  }

  private resolveBlockPeriod(period: string | null | undefined, label: string | null | undefined): PeakReportBlockDefinition['period'] {
    return resolveReportBlockPeriod(period, label, resolvePeriodForBlockLabel);
  }

  private extractReport(response: unknown): PeakReportReportResponse | null {
    const value = this.extractResponseValue(response);
    if (!value) {
      return null;
    }

    const blocks = this.extractBlockDefinitions(value);
    const rows = this.extractRows(value);
    const summary = this.extractSummary(value);
    const dataDate = this.extractStringValue(value, 'data_date') ?? this.getSelectedDateValue();
    const dateLabel = this.preferencesSvc.formatDateOnly(dataDate);

    return {
      data_date: dataDate,
      date_label: dateLabel,
      blocks,
      rows,
      summary,
      filters: {
        entity_id: null,
        substation_id: this.normalizeNumber(this.filtersForm.get('substation_id')?.value),
      },
    };
  }

  private extractBlockDefinitions(value: unknown): PeakReportBlockDefinition[] {
    return extractReportBlockDefinitions(
      value,
      (source, key) => this.getArrayValue(source, key),
      (source) => this.getRecord(source),
      (source) => this.normalizeNumber(source),
      (source, key) => this.extractStringValue(source, key),
      (period, label) => this.resolveBlockPeriod(period, label),
      (record, block) => ({ ...block, is_peak: record['is_peak'] === true }),
    );
  }

  private extractRows(value: unknown): PeakReportRow[] {
    return extractReportRows(
      value,
      {
        getArray: (source, key) => this.getArrayValue(source, key),
        getRecord: (source) => this.getRecord(source),
        normalizeNumber: (source) => this.normalizeNumber(source),
        extractString: (source, key) => this.extractStringValue(source, key),
        resolvePeriod: (period, label) => this.resolveBlockPeriod(period, label),
        mapBlock: (record, block) => ({
          ...block,
          source: block.source as PeakReportBlockCell['source'],
          is_peak: record['is_peak'] === true,
        }),
        mapRow: (record, row) => ({
          ...row,
          installed_capacity: this.normalizeNullableNumber(record['installed_capacity']),
          row_kind: this.extractStringValue(record, 'row_kind') as PeakReportRow['row_kind'],
          parent_row_key: this.extractStringValue(record, 'parent_row_key'),
          row_theme: this.extractStringValue(record, 'row_theme'),
          is_small_power: Boolean(record['is_small_power']),
        }),
      },
    );
  }

  private extractSummary(value: unknown): PeakReportSummary {
    return extractReportSummary(value, (source, key) => this.getRecordValue(source, key), (source) => this.getRecord(source), (source) => this.normalizeNumber(source));
  }

  private getSelectedDateValue(): string {
    return this.selectedDateAdValue || getReportSelectedDateFormValue(this.filtersForm, getTodayDateValue());
  }

  private getSelectedDateFormValue(): string {
    return getReportSelectedDateFormValue(this.filtersForm, getTodayDateValue());
  }

  protected openDatePicker(): void {
    openReportDatePicker(
      this.isBsDateMode,
      this.bsCalendarOpen,
      () => this.closeBsDatePicker(),
      () => void this.openBsDatePicker(),
      this.hiddenDateInput?.nativeElement ?? null,
    );
  }

  protected closeBsDatePicker(): void {
    this.bsCalendarOpen = false;
  }

  protected selectBsCalendarCell(cell: PeakReportBsCalendarCell): void {
    if (!cell?.ad_date) {
      return;
    }

    this.closeBsDatePicker();
    this.filtersForm.patchValue({ date: cell.ad_date }, { emitEvent: true });
  }

  protected shiftBsCalendarMonth(offset: number): void {
    if (!this.bsCalendarMonth || !Number.isFinite(offset) || !offset) {
      return;
    }

    const nextMonth = this.bsCalendarMonth.bs_month + offset;
    let nextYear = this.bsCalendarMonth.bs_year;
    let normalizedMonth = nextMonth;

    if (nextMonth < 1) {
      normalizedMonth = 12;
      nextYear -= 1;
    } else if (nextMonth > 12) {
      normalizedMonth = 1;
      nextYear += 1;
    }

    this.loadBsCalendar(`${nextYear.toString().padStart(4, '0')}-${normalizedMonth.toString().padStart(2, '0')}-01`, 'BS');
  }

  private async openBsDatePicker(): Promise<void> {
    this.bsCalendarOpen = true;
    const currentDate = this.getSelectedDateValue() || this.getSelectedDateFormValue();
    const currentKey = this.getSelectedDateValue() === this.selectedDateAdValue ? 'AD' : 'BS';
    await this.loadBsCalendar(currentDate, currentKey);
  }

  private async loadBsCalendar(bsDateValue: string, bsAdKey: 'AD' | 'BS' = 'BS'): Promise<void> {
    await loadReportBsCalendar({
      dateValue: bsDateValue,
      requestCalendar: () => firstValueFrom(this.httpSvc.requestCall(
        AuthEndPoints.GET_LOG_SHEET_BS_CALENDAR,
        ApiMethod.POST,
        { data_date: bsDateValue, bs_ad_key: bsAdKey },
        false,
        { suppressToast: true },
      )),
      extractCalendar: (response) => extractReportBsCalendar(response),
      setLoading: (loading) => {
        this.bsCalendarLoading = loading;
      },
      setCalendar: (calendar) => {
        this.bsCalendarMonth = calendar;
      },
      detectChanges: () => this.cdr.detectChanges(),
    });
  }

  private async handleDateSelectionChange(preserveViewState = false): Promise<void> {
    await handleReportDateSelection({
      adDate: this.getSelectedDateFormValue(),
      showBsDate: this.preferencesSvc.showBsDate,
      convertDate: (date) => firstValueFrom(this.httpSvc.requestCall(
        AuthEndPoints.CONVERT_LOG_SHEET_DATE,
        ApiMethod.POST,
        { data_date: date, bs_ad_key: 'BS', source_bs_ad_key: 'AD' },
        false,
        { suppressToast: true },
      )).then((response) => extractReportDateConversion(response)),
      formatDate: (date) => this.preferencesSvc.formatDateOnly(date),
      fallbackDate: getTodayDateValue(),
      setAdDate: (date) => {
        this.selectedDateAdValue = date;
      },
      setSelection: (requestValue, displayLabel) => {
        this.selectedDateRequestValue = requestValue;
        this.selectedDateDisplayLabel = displayLabel;
      },
      loadReport: (preserve) => this.loadReport(preserve),
      preserveViewState,
    });
  }

}
